import { TradableAreaRegistrationIssue_MissingMaster } from '@ebalance/proto/bffuser/v1/tradable_area_service_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { TertiaryButton } from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationSolid } from '@enechain/ecloud-icons'

import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import { convertAreaLabel } from '~/models/area'
import type { IntradayAreaEmptyState as EmptyState } from '~/pages/IntradayBidding/models/areaEmptyState'
import { routePaths } from '~/routes/routePaths'

type Props = { state: EmptyState }

type ListGateProps = {
  state?: EmptyState
  children: React.ReactNode
}

/** AC-BIN-111: 周辺の板・チャート・作成導線を残し、入札一覧だけを空状態へ差し替える。 */
export const IntradayBidListAreaGate = ({
  state,
  children,
}: ListGateProps): React.ReactNode =>
  state === undefined ? children : <IntradayAreaEmptyState state={state} />

export const IntradayAreaEmptyState = ({
  state,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  if (state.kind === 'addToBusinessAreas') {
    const areas = state.orderableAreaCodes.map(convertAreaLabel).join('・')
    return (
      <EmptyPanel
        action={
          <TertiaryButton asChild>
            <RouterLink to={`${routePaths.settings.path}?focus=business-area`}>
              {t('page:biddingIntraday.areaEmpty.addToBusinessArea.action')}
            </RouterLink>
          </TertiaryButton>
        }
        description={t(
          'page:biddingIntraday.areaEmpty.addToBusinessArea.description',
          { areas },
        )}
        icon={
          <FaTriangleExclamationSolid
            boxSize={EMPTY_PANEL_ICON_SIZE}
            aria-hidden="true"
          />
        }
        title={t('page:biddingIntraday.areaEmpty.addToBusinessArea.title')}
      />
    )
  }

  const masterName = state.missingMasters
    .map((master) => {
      switch (master) {
        case TradableAreaRegistrationIssue_MissingMaster.SUBMITTER:
          return t('page:biddingIntraday.areaEmpty.master.submitter')
        case TradableAreaRegistrationIssue_MissingMaster.DEMAND_PROCUREMENT:
          return t('page:biddingIntraday.areaEmpty.master.demandProcurement')
        case TradableAreaRegistrationIssue_MissingMaster.BG:
          return t('page:biddingIntraday.areaEmpty.master.bg')
        default:
          return ''
      }
    })
    .filter(Boolean)
    .join(t('page:biddingIntraday.areaEmpty.master.separator'))
  const displayMasterName =
    masterName || t('page:biddingIntraday.areaEmpty.master.fallback')
  return (
    <EmptyPanel
      description={t(
        'page:biddingIntraday.areaEmpty.masterNotRegistered.description',
        {
          issuer: issuerTypeLabel(state.issuerType),
          masterName: displayMasterName,
        },
      )}
      icon={
        <FaTriangleExclamationSolid
          boxSize={EMPTY_PANEL_ICON_SIZE}
          aria-hidden="true"
        />
      }
      title={t('page:biddingIntraday.areaEmpty.masterNotRegistered.title', {
        masterName: displayMasterName,
      })}
    />
  )
}
